import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { mainSiteUrl } from "@/mocks/categories";

const siteUrl = "https://sugargoosheet.casa";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Sugargoo Spreadsheet Complete Guide 2026: The Ultimate Product Research System",
  description: "A comprehensive step-by-step guide to mastering the Sugargoo Spreadsheet — from understanding what it is to building advanced product research workflows.",
  image: "https://readdy.ai/api/search-image?query=Professional%20organized%20workspace%20with%20multiple%20product%20samples%20sneakers%20clothing%20accessories%20arranged%20on%20clean%20white%20desk%20with%20laptop%20displaying%20spreadsheet%20data%20warm%20natural%20lighting%20editorial%20productivity%20scene%20minimalist%20modern%20aesthetic&width=1400&height=700&seq=blog-featured-spreadsheet-v2&orientation=landscape",
  datePublished: "2026-08-09",
  dateModified: "2026-08-09",
  author: {
    "@type": "Organization",
    name: "Sugargoo Spreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "Sugargoo Spreadsheet",
    url: siteUrl,
  },
  inLanguage: "en",
  isPartOf: {
    "@type": "WebSite",
    name: "Sugargoo Spreadsheet",
    url: siteUrl,
  },
};

const tableOfContents = [
  "What Is Sugargoo Spreadsheet?",
  "Why You Need an Organized Product Research System",
  "Key Features of a Quality Sugargoo Spreadsheet",
  "How to Use Sugargoo Spreadsheet Effectively",
  "Product Categories You Can Find",
  "Benefits for Dropshippers and Resellers",
  "Sugargoo Spreadsheet vs Manual Research",
  "Common Mistakes to Avoid",
  "Tips for Maximizing Your Spreadsheet",
  "Frequently Asked Questions",
];

export default function BlogArticle() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">Sugargoo Spreadsheet Complete Guide 2026</span>
            </nav>

            {/* Header */}
            <header className="mb-10">
              <div className="inline-block text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full mb-4">
                Ultimate Guide
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                Sugargoo Spreadsheet Complete Guide 2026: The Ultimate Product Research System
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
                A comprehensive, step-by-step guide to mastering the Sugargoo Spreadsheet — from understanding what it is 
                to building advanced product research workflows that help you discover winning products before your competition.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> Updated: August 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> 12 min read
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-book-open-line"></i> 2,500+ words
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20organized%20workspace%20with%20multiple%20product%20samples%20sneakers%20clothing%20accessories%20arranged%20on%20clean%20white%20desk%20with%20laptop%20displaying%20spreadsheet%20data%20warm%20natural%20lighting%20editorial%20productivity%20scene%20minimalist%20modern%20aesthetic&width=1400&height=700&seq=blog-featured-spreadsheet-v2&orientation=landscape"
                alt="Sugargoo Spreadsheet Complete Guide 2026 - Product Research System"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Table of Contents */}
            <nav className="bg-background-100 border border-background-200/70 rounded-xl p-5 mb-10" aria-label="Table of Contents">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">Table of Contents</h2>
              <ol className="space-y-1.5">
                {tableOfContents.map((item, i) => (
                  <li key={i} className="text-xs text-foreground-600 hover:text-primary-500 transition-colors">
                    <a href={`#section-${i + 1}`} className="flex items-center gap-2">
                      <span className="text-primary-400 font-medium">{i + 1}.</span> {item}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Section 1 */}
            <section id="section-1" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                What Is Sugargoo Spreadsheet?
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                A <strong className="text-foreground-900">Sugargoo Spreadsheet</strong> is a meticulously organized digital document 
                that consolidates product listings, supplier information, pricing data, shipping estimates, and quality ratings 
                from China&apos;s largest e-commerce platforms. Rather than spending countless hours manually browsing through Taobao, 
                1688, or Weidian, users can leverage a pre-built spreadsheet to instantly access curated product data organized 
                by category, price range, supplier rating, and shipping availability.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                The spreadsheet serves as a central hub for product research — think of it as your personal sourcing assistant 
                that has already done the heavy lifting of finding, organizing, and comparing products. For anyone involved in 
                dropshipping, reselling, or personal shopping from China, having access to a well-maintained 
                <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium"> Sugargoo Spreadsheet</Link> can 
                reduce research time by up to 80% compared to traditional manual browsing methods.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                What makes the Sugargoo Spreadsheet particularly powerful is its structure. Products are not just listed — they 
                are categorized, tagged with relevant keywords, linked to supplier pages, and often accompanied by notes on 
                pricing trends and quality assessments. This structured approach transforms chaotic product discovery into a 
                systematic, repeatable process that anyone can follow regardless of experience level.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Why You Need an Organized Product Research System
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                The Chinese e-commerce ecosystem is vast and complex. Taobao alone hosts over one billion product listings, 
                and 1688 serves as the backbone of global wholesale supply chains. Without a structured system, navigating 
                this ocean of products is not just time-consuming — it is genuinely overwhelming.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Here is what happens when you rely on manual research: you open dozens of browser tabs, lose track of which 
                supplier had the better price, forget to save product links, mix up shipping estimates, and eventually settle 
                for suboptimal choices simply because the process became too chaotic. This is the reality for most beginners 
                who attempt China sourcing without an organized system.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                A structured spreadsheet eliminates all of these pain points. Every product is tracked with consistent fields: 
                product name, category, supplier link, price, shipping weight, estimated delivery time, minimum order quantity, 
                and personal notes. This consistency means you can sort, filter, and compare products with a few clicks — 
                something that is impossible when your research is scattered across browser bookmarks and mental notes. 
                For a deeper dive into how to build your own system, check out our 
                <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium"> complete resource hub</Link>.
              </p>
            </section>

            {/* Section 3 */}
            <section id="section-3" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Key Features of a Quality Sugargoo Spreadsheet
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Not all spreadsheets are created equal. A high-quality Sugargoo Spreadsheet distinguishes itself through 
                several essential features that separate professional-grade tools from amateur collections.
              </p>
              <div className="space-y-3 mb-4">
                {[
                  { title: "Comprehensive Product Categories", desc: "A quality spreadsheet covers multiple categories — shoes, hoodies, t-shirts, jackets, bags, accessories, electronics, and more. Each category should contain enough products to give you genuine options, not just a handful of token listings." },
                  { title: "Supplier Verification and Ratings", desc: "The best spreadsheets include supplier ratings based on transaction history, response rates, and product quality assessments. This information helps you avoid unreliable suppliers and focus on verified sellers with proven track records." },
                  { title: "Pricing Transparency", desc: "Price data should include both the base product price and estimated shipping costs. Some spreadsheets even include historical pricing data, helping you identify seasonal trends and optimal buying windows." },
                  { title: "Regular Updates", desc: "China sourcing is dynamic — products trend, suppliers change, and prices fluctuate. A spreadsheet that was created six months ago without updates is essentially obsolete. Look for spreadsheets that commit to regular refresh cycles." },
                  { title: "User-Friendly Organization", desc: "Clear column headers, consistent formatting, logical category groupings, and filter-friendly layouts make the difference between a spreadsheet you actually use and one that collects digital dust." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg">
                    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-50 mt-0.5">
                      <span className="text-xs font-bold text-primary-500">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 4 */}
            <section id="section-4" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                How to Use Sugargoo Spreadsheet Effectively
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Having the spreadsheet is only half the battle — knowing how to extract maximum value from it is what 
                separates successful sourcers from those who struggle. Here is a practical workflow that experienced 
                users follow.
              </p>
              <div className="space-y-4">
                {[
                  { step: "Step 1", title: "Identify Your Niche and Budget First", desc: "Before opening the spreadsheet, define what you are looking for. Are you sourcing sneakers for a streetwear brand? Budget-friendly accessories for a general store? Having clear criteria prevents endless scrolling and decision paralysis." },
                  { step: "Step 2", title: "Filter by Category and Price Range", desc: "Use the spreadsheet filtering tools to narrow down products to your specific category and budget. This immediately eliminates irrelevant listings and lets you focus on viable options." },
                  { step: "Step 3", title: "Compare Suppliers Side by Side", desc: "For each product that catches your eye, compare the supplier information with similar products from other suppliers. Look at ratings, minimum order quantities, and shipping options before making decisions." },
                  { step: "Step 4", title: "Check Shipping and Logistics", desc: "A great product at a great price means nothing if shipping costs eat your margins or delivery times are unacceptable. Always cross-reference the shipping estimates in the spreadsheet with current carrier rates." },
                  { step: "Step 5", title: "Document Your Own Findings", desc: "As you use the spreadsheet, add your own notes and observations. Which suppliers were responsive? Which products received good feedback? Your personal annotations make the spreadsheet increasingly valuable over time." },
                ].map((s, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center rounded-xl bg-primary-50">
                      <span className="text-xs font-bold text-primary-500 text-center leading-tight">{s.step}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{s.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 5 */}
            <section id="section-5" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Product Categories You Can Find
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                The Sugargoo Spreadsheet covers an extensive range of product categories, making it a versatile tool 
                regardless of your niche. From fashion and footwear to electronics and accessories, the spreadsheet 
                provides organized listings across all major product segments that sell well internationally.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Popular categories include shoes — from athletic sneakers to casual footwear — hoodies and sweaters for 
                streetwear brands, t-shirts in various styles and fabrics, jackets suitable for different seasons, pants 
                and shorts for complete outfit sourcing, headwear including caps and beanies, matched sets for coordinated 
                looks, underwear and underpants for essentials, sports jerseys for athletic niches, accessories ranging 
                from jewelry to phone cases, bags including backpacks and handbags, belts in various materials, perfumes 
                and fragrances, and a miscellaneous category for unique finds that do not fit standard classifications.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Each category in the spreadsheet is populated with products that have demonstrated market demand, 
                reasonable pricing, and reliable supplier availability. This curation saves you from the endless 
                scrolling and guesswork that characterizes unfiltered platform browsing. Explore all categories on 
                our <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">homepage category section</Link>.
              </p>
            </section>

            {/* Section 6 */}
            <section id="section-6" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Benefits for Dropshippers and Resellers
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                For dropshippers and resellers, the Sugargoo Spreadsheet is not just a convenience — it is a competitive advantage. 
                The ability to quickly identify trending products, compare supplier pricing, and make data-driven sourcing 
                decisions directly impacts your bottom line.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {[
                  { title: "Faster Product Discovery", desc: "Cut research time from hours to minutes. The pre-organized structure means you go straight to evaluation instead of spending time on discovery." },
                  { title: "Better Profit Margins", desc: "Side-by-side supplier comparison helps you find the best price-quality ratio, directly improving your margins on every product you sell." },
                  { title: "Reduced Risk", desc: "Supplier ratings and verification data help you avoid problematic sellers, reducing the risk of quality issues, shipping delays, or outright scams." },
                  { title: "Scalable Workflow", desc: "As your business grows, the spreadsheet scales with you. Add more products, track more suppliers, and manage larger inventories without increasing complexity." },
                ].map((b, i) => (
                  <div key={i} className="p-4 bg-background-100 border border-background-200/70 rounded-lg">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{b.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 7 */}
            <section id="section-7" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Sugargoo Spreadsheet vs Manual Research
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                The difference between using a structured spreadsheet and conducting manual research is striking. 
                Let us break down the comparison across the dimensions that matter most for your sourcing efficiency.
              </p>
              <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-900">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Aspect</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Manual Research</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-primary-300">Sugargoo Spreadsheet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Time per product", "15-30 minutes", "1-3 minutes"],
                      ["Supplier comparison", "Difficult, scattered data", "Instant, side-by-side"],
                      ["Price tracking", "Manual, error-prone", "Automated, accurate"],
                      ["Organization", "Chaotic, lost information", "Structured, searchable"],
                      ["Scalability", "Does not scale well", "Scales effortlessly"],
                      ["Learning curve", "Steep, trial and error", "Minimal, guided structure"],
                    ].map((row, i) => (
                      <tr key={i} className={`border-t border-background-200/70 ${i % 2 === 0 ? "bg-background-50" : "bg-background-100"}`}>
                        <td className="px-4 py-3 text-xs font-medium text-foreground-900">{row[0]}</td>
                        <td className="px-4 py-3 text-xs text-foreground-500">{row[1]}</td>
                        <td className="px-4 py-3 text-xs text-primary-600 font-medium">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 8 */}
            <section id="section-8" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Common Mistakes to Avoid
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Even with a great spreadsheet at your disposal, certain mistakes can undermine your sourcing success. 
                Being aware of these pitfalls helps you avoid them from the start.
              </p>
              <div className="space-y-3">
                {[
                  { title: "Skipping supplier verification", desc: "Never rely solely on the spreadsheet listing. Always cross-check supplier ratings, read recent reviews, and if possible, order samples before committing to large quantities." },
                  { title: "Ignoring shipping costs in margin calculations", desc: "The product price is only part of the equation. Shipping, customs duties, and handling fees can significantly impact your final costs. Always calculate total landed cost." },
                  { title: "Not updating your own copy of the spreadsheet", desc: "If you are working with a downloaded version, make sure you have the latest update. Outdated pricing and supplier information can lead to costly mistakes." },
                  { title: "Overlooking product quality indicators", desc: "Price alone should not drive your decisions. Pay attention to material specifications, size charts, and quality notes included in the spreadsheet to avoid customer complaints." },
                  { title: "Trying to cover too many categories at once", desc: "Start with one or two categories that align with your target market. Spreading yourself too thin across multiple categories reduces your ability to make informed decisions in any of them." },
                ].map((m, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-red-50/30 border border-red-100 rounded-lg">
                    <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className="ri-error-warning-line text-red-400 text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{m.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 9 */}
            <section id="section-9" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                Tips for Maximizing Your Sugargoo Spreadsheet
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Beyond the basics, here are advanced strategies that experienced users employ to squeeze every ounce 
                of value from their spreadsheet.
              </p>
              <ul className="space-y-2.5">
                {[
                  "Create a personal copy and customize columns to match your specific workflow and tracking needs.",
                  "Use color coding to highlight products at different decision stages: green for approved, yellow for pending review, red for rejected.",
                  "Set up price alerts by periodically comparing current market prices against the spreadsheet reference prices.",
                  "Build a shortlist of 10-20 go-to suppliers based on consistent quality and reliability, then prioritize their products.",
                  "Cross-reference product trends on social media platforms to validate demand before sourcing from the spreadsheet.",
                  "Maintain a separate tab for seasonal products, tracking when certain items become available and when they sell out.",
                ].map((tip, i) => (
                  <li key={i} className="flex gap-3 text-sm text-foreground-600">
                    <i className="ri-check-fill text-primary-500 flex-shrink-0 mt-0.5"></i>
                    <span className="leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Section 10 - FAQ */}
            <section id="section-10" className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                Frequently Asked Questions About Sugargoo Spreadsheet
              </h2>
              <div className="space-y-3">
                {[
                  { q: "Is Sugargoo Spreadsheet free?", a: "Some basic versions are available for free, while comprehensive, regularly updated spreadsheets typically involve a one-time or subscription cost. The investment is generally worthwhile given the time savings and improved sourcing decisions." },
                  { q: "How often should I update my spreadsheet?", a: "For competitive niches like fashion and electronics, aim to update your spreadsheet at least monthly. For more stable categories, a quarterly refresh may be sufficient. The key is consistency — stale data leads to poor decisions." },
                  { q: "Can beginners use Sugargoo Spreadsheet effectively?", a: "Absolutely. The spreadsheet is designed to be intuitive. Most beginners can start finding products within minutes of opening it. The structured format actually makes it easier for newcomers than experienced sourcers who are used to chaotic manual methods." },
                  { q: "What is the difference between Sugargoo Spreadsheet and browsing Taobao directly?", a: "Browsing Taobao directly gives you access to everything but with no organization. The spreadsheet provides curated, organized data that has already been filtered for quality, pricing, and supplier reliability — saving you the most time-consuming part of the research process." },
                  { q: "Can I use Sugargoo Spreadsheet for my dropshipping store?", a: "Yes, many successful dropshippers rely on structured spreadsheets for product research. The organized data helps you quickly identify products with good margins, reliable suppliers, and reasonable shipping times — all critical factors for dropshipping success." },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-100 border border-background-200/70 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-xs text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* CTA */}
            <div className="bg-background-900 rounded-2xl p-8 md:p-10 text-center mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
                Ready to Start Your Product Research Journey?
              </h2>
              <p className="text-sm text-background-400 max-w-md mx-auto mb-6">
                Get access to the complete Sugargoo Spreadsheet and transform how you discover products from China.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-900 bg-primary-400 hover:bg-primary-300 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Get Sugargoo Spreadsheet
                </a>
                <Link
                  to="/"
                  className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all duration-200 whitespace-nowrap"
                >
                  Back to Homepage
                </Link>
              </div>
            </div>

            {/* Related Links */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Related Resources</h3>
              <ul className="space-y-1.5">
                <li>
                  <Link to="/" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">
                    Sugargoo Spreadsheet Homepage — Browse all categories and resources
                  </Link>
                </li>
                <li>
                  <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-primary-500 transition-colors">
                    Buy Sugargoo Spreadsheet on kaspreadsheet.com
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}